import { useMemo, useState } from 'react';
import { Input, Pagination, Space, Tag, type TableColumnsType } from 'antd';
import { Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { channelApi } from '@/api/channel.api';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import {
  ChannelStatus,
  ChannelType,
  ChatType,
  type Channel,
} from '@/proto/agentplatform/v1/channel_pb';
import ProTable from '@/components/pro/ProTable';
import DsIcon from '@/components/Layout/DsIcon';
import { formatTimestamp } from '@/utils/utils';

const typeLabel: Partial<Record<ChannelType, string>> = {
  [ChannelType.FEISHU]: '飞书',
  [ChannelType.QQ]: 'QQ',
  [ChannelType.BRIDGE]: 'WebSocket',
};

function formatOfflineAt(ts: bigint | undefined) {
  if (!ts || ts === 0n) return '—';
  return formatTimestamp(ts);
}

export default function ChannelsPage() {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(200);

  const { data, isLoading } = useQuery({
    queryKey: ['channels', page, pageSize],
    queryFn: () =>
      channelApi.listChannels(
        create(ListRequestSchema, {
          page,
          pageSize,
        })
      ),
  });

  const rows = (data?.data || []).filter(Boolean);
  const totalCount = data?.total ?? 0;

  const filtered = useMemo(() => {
    const keyword = searchKeyword.toLowerCase().trim();
    if (!keyword) return rows;
    return rows.filter((ch) => ch.name.toLowerCase().includes(keyword));
  }, [rows, searchKeyword]);

  const columns: TableColumnsType<Channel> = [
    {
      title: '名称',
      key: 'name',
      render: (_, row) => (
        <div className="pp-cell-name">
          <span
            className="obj-icon-tinted"
            style={{ ['--c' as string]: 'var(--platform-c-channel)' }}
          >
            <DsIcon
              name={row.type === ChannelType.QQ ? 'message-square' : 'message-circle'}
              size={15}
            />
          </span>
          <div>
            <div className="name-text">{row.name || '—'}</div>
            <div className="id-text">{row.id}</div>
          </div>
        </div>
      ),
    },
    {
      title: '类型',
      key: 'type',
      width: 100,
      render: (_, row) => (
        <Tag bordered={false}>{typeLabel[row.type] ?? ChannelType[row.type] ?? '—'}</Tag>
      ),
    },
    {
      title: '能力',
      key: 'capabilities',
      render: (_, row) => {
        const caps = row.capabilities;
        if (!caps) return <span style={{ color: 'var(--text-tertiary, #999)' }}>—</span>;
        const chatTypes = caps.chatTypes ?? [];
        const hasP2P = chatTypes.includes(ChatType.P2P);
        const hasGroup = chatTypes.includes(ChatType.GROUP);
        return (
          <Space size={4} wrap>
            {hasP2P && <Tag bordered={false}>单聊</Tag>}
            {hasGroup && <Tag bordered={false}>群聊</Tag>}
            {!hasP2P && !hasGroup && <Tag bordered={false}>未声明聊天类型</Tag>}
            <Tag color={caps.media ? 'processing' : 'default'} bordered={false}>
              {caps.media ? '支持多媒体' : '不支持多媒体'}
            </Tag>
            <Tag color={caps.streaming ? 'processing' : 'default'} bordered={false}>
              {caps.streaming ? '支持流式' : '不支持流式'}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, row) => {
        if (row.status === ChannelStatus.ONLINE) return <Tag color="success">在线</Tag>;
        if (row.status === ChannelStatus.OFFLINE) return <Tag color="default">离线</Tag>;
        return <Tag color="default">—</Tag>;
      },
    },
    {
      title: '首次上线',
      key: 'firstSeenAt',
      width: 160,
      render: (_, row) => formatTimestamp(row.firstSeenAt),
    },
    {
      title: '最后离线',
      key: 'lastOfflineAt',
      width: 160,
      render: (_, row) => formatOfflineAt(row.lastOfflineAt),
    },
  ];

  return (
    <div className="pp-page">
      <header className="pp-head">
        <div>
          <h2>渠道</h2>
          <p>平台支持的渠道类型注册表（接入哪些 IM / 协议）</p>
        </div>
        <Input
          placeholder="按名称搜索"
          style={{ width: 240 }}
          allowClear
          prefix={<Search size={14} />}
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
        />
      </header>

      <ProTable<Channel>
        data={filtered}
        loading={isLoading}
        rowKey={(row) => row.id || `${row.name}-${row.type}`}
        columns={columns}
        emptyText="暂无渠道"
      />

      {!isLoading && totalCount > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={totalCount}
            showSizeChanger
            pageSizeOptions={[10, 20, 50, 100]}
            showTotal={(t) => `共 ${t} 条`}
            onChange={(p, ps) => {
              setPage(p);
              setPageSize(ps);
            }}
          />
        </div>
      )}
    </div>
  );
}
